import React, { useEffect, useState } from 'react';
import { Typography, Button, Paper, Grid } from '@material-ui/core';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import ErrorIcon from '@material-ui/icons/Error';
import Feedback from '../Feedback/Feedback';
import OriginService from '../../services/OriginService/OriginService'


const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    padding: theme.spacing(2),
    [theme.breakpoints.up(600 + theme.spacing(3) * 2)]: {
      marginTop: theme.spacing(6),
      marginBottom: theme.spacing(6),
      marginLeft: theme.spacing(6),
      marginRight: theme.spacing(6),
      padding: theme.spacing(3),
    },
  },
  icon: {
    fontSize: '80px',
    color: '#c3c8cb',
    marginBottom: theme.spacing(2),
  },
  refreshButton: {
    marginTop: '20px',
    fontSize: '14px',
    textTransform: 'capitalize',
    borderRadius: '100px',
    paddingLeft: '2rem',
    paddingRight: '2rem',
    backgroundColor: '#79232e',
    fontWeight: 700,
    color: '#fff',
    '&:hover': {
      color: '#fff',
      backgroundColor: '#a12e3d'
    },
    '&:active': {
      color: '#fff',
      backgroundColor: '#79232e'
    },
    '&:focus': {
      color: '#fff',
      backgroundColor: '#79232e'
    }
  }
}));

const LoginPage = (props) => {
  const [feedbackMessage, setFeedbackMessage] = useState('Loading...'); 
  const [loadingStatus, setLoadingStatus] = useState('LOADING');
  const classes = useStyles(); 

  const host = props.host;

  const launchApplication = async () => {
    const proxy = await host;

    setTimeout(() => {
      setFeedbackMessage('Connecting to Encompass Partner Connect...');
    }, 1000);



    setTimeout(() => {
      setFeedbackMessage('Requesting authentication token...');
    }, 2000);


    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Requesting authentication token timed out.')), 5000)
    );

    try {
      const originContext = await Promise.race([proxy.getTransactionOrigin(), timeoutPromise]);
      const originId = originContext.id;
      const partnerAccessToken = originContext.partnerAccessToken;
      const currentTransactionId = originContext.transactionId;
      authenticateUser(originId, partnerAccessToken, currentTransactionId);
    } catch (error) {
      if (error.message === 'Requesting authentication token timed out.') {
        setLoadingStatus('TIMEOUT');
      } else {
        setLoadingStatus('ORIGIN_ERROR');
      }
    }
  };

  const authenticateUser = async (originId, partnerAccessToken, currentTransactionId) => {
    const origin = OriginService(originId, partnerAccessToken);
    const originLoanData = await origin.getOriginLoanData();
    if (originLoanData.status == true) {
      props.onlogin(currentTransactionId);
      setLoadingStatus('LOGIN');
    }
    else {
      handleLoanError(originLoanData);
    }
  };

  const handleLoanError = (originLoanData) => {
    if (originLoanData.message === "Invalid Credentials") {
      setLoadingStatus('LOGIN FAILED');
      launchEpcLoginScreen();
    } else  {
      setLoadingStatus('ERROR');
    }
  };

  const launchEpcLoginScreen = async () => {
    const proxy = await host;
    const userLogin = await proxy.performAction();
    if (userLogin && userLogin.username && userLogin.password) {
      const originContext = await proxy.refreshOrigin();
      const originId = originContext.id;
      const partnerAccessToken = originContext.partnerAccessToken;
      const currentTransactionId = originContext.transactionId;
      authenticateUser(originId, partnerAccessToken, currentTransactionId);
    }
    else {
      await proxy.close();
    }
  }

  const handleRefresh = () => {
    window.location.reload();
  };


  useEffect(() => {
    document.title = "Login - Encompass Partner Connect";
    launchApplication();

  }, []);

  return (
    <React.Fragment>

      {loadingStatus === 'LOADING' && (
        <Paper className={classes.paper}>
          <Grid
            container
            spacing={0}
            direction='column'
            alignItems='center'
            justify='center'
            style={{ minHeight: '40vh' }}>
            <Grid item xs={3}>
              <Feedback message={feedbackMessage} />
            </Grid>
          </Grid>
        </Paper>
      )}

      {loadingStatus === 'TIMEOUT' && (
        <Paper className={classes.paper}>
          <Grid
            container
            spacing={0}
            direction='column'
            alignItems='center'
            justify='center'
            style={{ minHeight: '40vh' }}>
            <Grid item xs={12} sm={8} md={6} style={{ textAlign: 'center' }}>
              <ErrorIcon className={classes.icon} style={{ fontSize: '4rem' }} />
              <Typography variant="h5" gutterBottom style={{ fontWeight: 'bold' }}>
                Requesting authentication token timed out.
              </Typography>
              <Button variant="contained" color="primary" onClick={handleRefresh} className={classes.refreshButton}>
                Refresh Page
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}
      {loadingStatus === 'ORIGIN_ERROR' && (
        <Paper className={classes.paper}>
          <Grid
            container
            spacing={0}
            direction='column'
            alignItems='center'
            justify='center'
            style={{ minHeight: '40vh' }}>
            <Grid item xs={12} sm={8} md={6} style={{ textAlign: 'center' }}>
              <ErrorIcon className={classes.icon} style={{ fontSize: '4rem' }} />
              <Typography variant="h4" gutterBottom style={{ color: '#79232e', fontWeight: 'bold' }}>
                Loan Information Unavailable
              </Typography>
              <Typography variant="body1" style={{ color: '#000000' }}>
                We are unable to retrieve your loan file information. Please contact your system administrator.
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {loadingStatus === 'ERROR' && (
        <Paper className={classes.paper}>
          <Grid
            container
            spacing={0}
            direction='column'
            alignItems='center'
            justify='center'
            style={{ minHeight: '40vh' }}>
            <Grid item xs={12} sm={8} md={6} style={{ textAlign: 'center' }}>
              <ErrorIcon className={classes.icon} style={{ fontSize: '4rem' }} />
              <Typography variant="h4" gutterBottom style={{ color: '#79232e', fontWeight: 'bold' }}>
              Something Went Wrong
              </Typography>
              <Typography variant="body1" style={{ color: '#000000' }}>
              An unexpected error occurred. Please try again later or contact your system administrator.
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}
    </React.Fragment>
  );
};

LoginPage.propTypes = {
  host: PropTypes.object.isRequired
};

export default LoginPage;
